import { createAsyncThunk } from "@reduxjs/toolkit";
import { ApiErrorResponse } from "../../../api/types/ApiErrorResponse";
import { toApiError } from "../../../api/utils/errorHandling";
import portalAxios, { storePortalSession, StoredPortalSession } from "../portalAxios";
import { PortalBalanceResponse } from "../contracts/PortalBalanceResponse";
import { PortalContractResponse } from "../contracts/PortalContractResponse";
import { PortalMandateResponse } from "../contracts/PortalMandateResponse";
import { MandateSetupSessionResponse } from "../../collection/contracts/MandateSetupSessionResponse";
import { PortalMeResponse } from "../contracts/PortalMeResponse";
import { PortalOccurrenceResponse } from "../contracts/PortalOccurrenceResponse";
import { PortalSessionResponse } from "../contracts/PortalSessionResponse";
import { PortalVisitResponse } from "../contracts/PortalVisitResponse";
import { PortalProgressResponse } from "../contracts/PortalProgressResponse";
import { mapResponseToProgress } from "../../training/mappers/TopicPlanResponseMapper";
import { Progress } from "../../training/models/Progress";
import { UnsubscribeResponse } from "../contracts/UnsubscribeResponse";
import { UpdateMemberNotificationPreferencesRequest } from "../contracts/UpdateMemberNotificationPreferencesRequest";
import { VerifyPortalCodeRequest } from "../contracts/VerifyPortalCodeRequest";
import { MemberNotificationPreferencesResponse } from "../../members/contracts/MemberNotificationPreferencesResponse";
import { AbsenceResponse } from "../../absences/contracts/AbsenceResponse";
import { MaterialDownloadUrlResponse } from "../../materials/contracts/MaterialDownloadUrlResponse";
import { PaginatedResult } from "../../sessions/contracts/PaginatedResult";
import { CreatePortalAbsenceRequest } from "../contracts/CreatePortalAbsenceRequest";
import { PortalAbsenceOverviewEntryResponse } from "../contracts/PortalAbsenceOverviewEntryResponse";
import { PortalMaterialResponse } from "../contracts/PortalMaterialResponse";
import { PortalOccurrence } from "../models/PortalOccurrence";
import { PortalOccurrenceResponseMapper } from "../mappers/PortalOccurrenceResponseMapper";
import { PortalMe } from "../models/PortalMe";
import { PortalMeResponseMapper } from "../mappers/PortalMeResponseMapper";
import { BookableOccurrenceResponse } from "../contracts/BookableOccurrenceResponse";
import { BookOccurrenceRequest } from "../contracts/BookOccurrenceRequest";
import { OccurrenceSeatResponse } from "../contracts/OccurrenceSeatResponse";

const occurrenceMapper = new PortalOccurrenceResponseMapper();

/** The session a redeem, verify or switch response signs in, with the rows the email reaches. */
export const toStoredPortalSession = (response: PortalSessionResponse): StoredPortalSession => ({
  memberId: response.memberId,
  firstName: response.firstName,
  expiresAtUtc: response.expiresAtUtc,
  contexts: response.contexts ?? [],
});
const meMapper = new PortalMeResponseMapper();

/** Requests a magic sign-in link. Always resolves — the API never reveals whether the email is known. */
export const requestPortalLink = createAsyncThunk<void, string, { rejectValue: ApiErrorResponse }>(
  "portal/requestLink",
  async (email, { rejectWithValue }) => {
    try {
      await portalAxios.post("/portal/auth/request-link", { email });
    } catch (error) {
      return rejectWithValue(toApiError(error));
    }
  },
);

/** Redeems a magic-link token; stores the portal session on success. */
export const redeemPortalToken = createAsyncThunk<
  PortalSessionResponse,
  string,
  { rejectValue: ApiErrorResponse }
>("portal/redeemToken", async (token, { rejectWithValue }) => {
  try {
    const response = await portalAxios.post<PortalSessionResponse>("/portal/auth/redeem", { token });
    storePortalSession(response.data.accessToken, toStoredPortalSession(response.data));
    return response.data;
  } catch (error) {
    return rejectWithValue(toApiError(error));
  }
});

/** Completes the login with the short code from the sign-in email; stores the portal session on success. */
export const verifyPortalCode = createAsyncThunk<
  PortalSessionResponse,
  VerifyPortalCodeRequest,
  { rejectValue: ApiErrorResponse }
>("portal/verifyCode", async (request, { rejectWithValue }) => {
  try {
    const response = await portalAxios.post<PortalSessionResponse>("/portal/auth/verify-code", request);
    storePortalSession(response.data.accessToken, toStoredPortalSession(response.data));
    return response.data;
  } catch (error) {
    return rejectWithValue(toApiError(error));
  }
});

export const fetchPortalMe = createAsyncThunk<PortalMe, void, { rejectValue: ApiErrorResponse }>(
  "portal/fetchMe",
  async (_, { rejectWithValue }) => {
    try {
      const response = await portalAxios.get<PortalMeResponse>("/portal/me");
      return meMapper.toPortalMe(response.data);
    } catch (error) {
      return rejectWithValue(toApiError(error));
    }
  },
);

export const fetchPortalOccurrences = createAsyncThunk<
  { memberId: string; occurrences: PortalOccurrence[] },
  { memberId: string; from: string; to: string },
  { rejectValue: ApiErrorResponse }
>("portal/fetchOccurrences", async ({ memberId, from, to }, { rejectWithValue }) => {
  try {
    const response = await portalAxios.get<PortalOccurrenceResponse[]>(
      `/portal/members/${memberId}/occurrences`,
      { params: { from, to } },
    );
    return { memberId, occurrences: occurrenceMapper.toItems(response.data) };
  } catch (error) {
    return rejectWithValue(toApiError(error));
  }
});

export const fetchPortalVisits = createAsyncThunk<
  { memberId: string; visits: PortalVisitResponse[] },
  { memberId: string; from: string; to: string },
  { rejectValue: ApiErrorResponse }
>("portal/fetchVisits", async ({ memberId, from, to }, { rejectWithValue }) => {
  try {
    const response = await portalAxios.get<PortalVisitResponse[]>(
      `/portal/members/${memberId}/visits`,
      { params: { from, to } },
    );
    return { memberId, visits: response.data };
  } catch (error) {
    return rejectWithValue(toApiError(error));
  }
});

/** The target's Progress on each live enrolment (read-only; self or ward). */
export const fetchPortalProgress = createAsyncThunk<
  { memberId: string; progress: Progress[] },
  string,
  { rejectValue: ApiErrorResponse }
>("portal/fetchProgress", async (memberId, { rejectWithValue }) => {
  try {
    const response = await portalAxios.get<PortalProgressResponse[]>(`/portal/members/${memberId}/progress`);
    return { memberId, progress: response.data.map(mapResponseToProgress) };
  } catch (error) {
    return rejectWithValue(toApiError(error));
  }
});

export const fetchPortalBalance = createAsyncThunk<
  PortalBalanceResponse,
  string,
  { rejectValue: ApiErrorResponse }
>("portal/fetchBalance", async (memberId, { rejectWithValue }) => {
  try {
    const response = await portalAxios.get<PortalBalanceResponse>(`/portal/members/${memberId}/balance`);
    return response.data;
  } catch (error) {
    return rejectWithValue(toApiError(error));
  }
});

export const fetchPortalMandate = createAsyncThunk<
  { memberId: string; mandate: PortalMandateResponse },
  string,
  { rejectValue: ApiErrorResponse }
>("portal/fetchMandate", async (memberId, { rejectWithValue }) => {
  try {
    const response = await portalAxios.get<PortalMandateResponse>(
      `/portal/members/${memberId}/mandate`,
    );
    return { memberId, mandate: response.data };
  } catch (error) {
    return rejectWithValue(toApiError(error));
  }
});

/** The member's contracts awaiting online acceptance. */
export const fetchPortalPendingContracts = createAsyncThunk<
  { memberId: string; contracts: PortalContractResponse[] },
  string,
  { rejectValue: ApiErrorResponse }
>("portal/fetchPendingContracts", async (memberId, { rejectWithValue }) => {
  try {
    const response = await portalAxios.get<PortalContractResponse[]>(
      `/portal/members/${memberId}/pending-contracts`,
    );
    return { memberId, contracts: response.data };
  } catch (error) {
    return rejectWithValue(toApiError(error));
  }
});

/** Records the signed-in member's (or guardian's) acceptance of one contract. */
export const acceptPortalContract = createAsyncThunk<
  { memberId: string; contract: PortalContractResponse },
  { memberId: string; contractId: string },
  { rejectValue: ApiErrorResponse }
>("portal/acceptContract", async ({ memberId, contractId }, { rejectWithValue }) => {
  try {
    const response = await portalAxios.post<PortalContractResponse>(
      `/portal/members/${memberId}/contracts/${contractId}/accept`,
    );
    return { memberId, contract: response.data };
  } catch (error) {
    return rejectWithValue(toApiError(error));
  }
});

/** Files a deletion request for the member; the portal session dies immediately. */
export const requestMemberDeletion = createAsyncThunk<
  void,
  string,
  { rejectValue: ApiErrorResponse }
>("portal/requestDeletion", async (memberId, { rejectWithValue }) => {
  try {
    await portalAxios.post(`/portal/members/${memberId}/deletion-request`);
  } catch (error) {
    return rejectWithValue(toApiError(error));
  }
});

/** Starts hosted SEPA mandate setup for the member; resolves to Stripe's page URL. */
export const createPortalMandateSetupSession = createAsyncThunk<
  string,
  string,
  { rejectValue: ApiErrorResponse }
>("portal/createMandateSetupSession", async (memberId, { rejectWithValue }) => {
  try {
    const response = await portalAxios.post<MandateSetupSessionResponse>(
      `/portal/members/${memberId}/mandate/setup-session`,
    );
    return response.data.url;
  } catch (error) {
    return rejectWithValue(toApiError(error));
  }
});

/** Anonymous path: redeems a mandate-setup link token into Stripe's hosted page URL. */
export const createMandateSetupSessionFromToken = createAsyncThunk<
  string,
  string,
  { rejectValue: ApiErrorResponse }
>("portal/createMandateSetupSessionFromToken", async (token, { rejectWithValue }) => {
  try {
    const response = await portalAxios.post<MandateSetupSessionResponse>(
      "/public/collection/mandate-setup/session",
      { token },
    );
    return response.data.url;
  } catch (error) {
    return rejectWithValue(toApiError(error));
  }
});

/** The signed-in member's own email settings. */
export const fetchPortalNotificationPreferences = createAsyncThunk<
  MemberNotificationPreferencesResponse,
  void,
  { rejectValue: ApiErrorResponse }
>("portal/fetchNotificationPreferences", async (_, { rejectWithValue }) => {
  try {
    const response = await portalAxios.get<MemberNotificationPreferencesResponse>(
      "/portal/notification-preferences",
    );
    return response.data;
  } catch (error) {
    return rejectWithValue(toApiError(error));
  }
});

/**
 * The member switching announcement email or session reminders on or off for
 * themselves. Only the switch that changed is sent; the other keeps its value.
 */
export const updatePortalNotificationPreferences = createAsyncThunk<
  MemberNotificationPreferencesResponse,
  UpdateMemberNotificationPreferencesRequest,
  { rejectValue: ApiErrorResponse }
>("portal/updateNotificationPreferences", async (request, { rejectWithValue }) => {
  try {
    const response = await portalAxios.put<MemberNotificationPreferencesResponse>(
      "/portal/notification-preferences",
      request,
    );
    return response.data;
  } catch (error) {
    return rejectWithValue(toApiError(error));
  }
});

/**
 * Anonymous path: the one-click unsubscribe link from an announcement or session
 * reminder email. The token decides which kind is switched off; the answer says which.
 * Uses portalAxios for its plain, token-free transport — there is no session here.
 */
export const unsubscribeFromAnnouncements = createAsyncThunk<
  UnsubscribeResponse,
  string,
  { rejectValue: ApiErrorResponse }
>("portal/unsubscribe", async (token, { rejectWithValue }) => {
  try {
    const response = await portalAxios.post<UnsubscribeResponse>(
      "/public/notifications/unsubscribe",
      { token },
    );
    return response.data;
  } catch (error) {
    return rejectWithValue(toApiError(error));
  }
});

/** The target member's current and upcoming absences (plus the recent past). */
export const fetchPortalAbsences = createAsyncThunk<
  { memberId: string; absences: AbsenceResponse[] },
  { memberId: string },
  { rejectValue: ApiErrorResponse }
>("portal/fetchAbsences", async ({ memberId }, { rejectWithValue }) => {
  try {
    const response = await portalAxios.get<AbsenceResponse[]>(
      `/portal/members/${memberId}/absences`,
    );
    return { memberId, absences: response.data };
  } catch (error) {
    return rejectWithValue(toApiError(error));
  }
});

export const createPortalAbsence = createAsyncThunk<
  { memberId: string; absence: AbsenceResponse },
  { memberId: string; request: CreatePortalAbsenceRequest },
  { rejectValue: ApiErrorResponse }
>("portal/createAbsence", async ({ memberId, request }, { rejectWithValue }) => {
  try {
    const response = await portalAxios.post<AbsenceResponse>(
      `/portal/members/${memberId}/absences`,
      request,
    );
    return { memberId, absence: response.data };
  } catch (error) {
    return rejectWithValue(toApiError(error));
  }
});

export const updatePortalAbsence = createAsyncThunk<
  { memberId: string; absence: AbsenceResponse },
  { memberId: string; absenceId: string; request: CreatePortalAbsenceRequest },
  { rejectValue: ApiErrorResponse }
>("portal/updateAbsence", async ({ memberId, absenceId, request }, { rejectWithValue }) => {
  try {
    const response = await portalAxios.put<AbsenceResponse>(
      `/portal/members/${memberId}/absences/${absenceId}`,
      request,
    );
    return { memberId, absence: response.data };
  } catch (error) {
    return rejectWithValue(toApiError(error));
  }
});

export const deletePortalAbsence = createAsyncThunk<
  { memberId: string; absenceId: string },
  { memberId: string; absenceId: string },
  { rejectValue: ApiErrorResponse }
>("portal/deleteAbsence", async ({ memberId, absenceId }, { rejectWithValue }) => {
  try {
    await portalAxios.delete(`/portal/members/${memberId}/absences/${absenceId}`);
    return { memberId, absenceId };
  } catch (error) {
    return rejectWithValue(toApiError(error));
  }
});

/** The shared org-wide absence calendar; only served when the organization switched it on. */
export const fetchPortalAbsenceOverview = createAsyncThunk<
  PortalAbsenceOverviewEntryResponse[],
  { from: string; to: string },
  { rejectValue: ApiErrorResponse }
>("portal/fetchAbsenceOverview", async ({ from, to }, { rejectWithValue }) => {
  try {
    const response = await portalAxios.get<PortalAbsenceOverviewEntryResponse[]>(
      "/portal/absences/overview",
      { params: { from, to } },
    );
    return response.data;
  } catch (error) {
    return rejectWithValue(toApiError(error));
  }
});

export const fetchPortalMaterials = createAsyncThunk<
  { memberId: string; materials: PortalMaterialResponse[] },
  { memberId: string },
  { rejectValue: ApiErrorResponse }
>("portal/fetchMaterials", async ({ memberId }, { rejectWithValue }) => {
  try {
    const response = await portalAxios.get<PaginatedResult<PortalMaterialResponse>>(
      `/portal/members/${memberId}/materials`,
      { params: { page: 1, pageSize: 100 } },
    );
    return { memberId, materials: response.data.items };
  } catch (error) {
    return rejectWithValue(toApiError(error));
  }
});

/** Uploads a file to the organization's shared materials (multipart form data). */
export const uploadPortalMaterial = createAsyncThunk<
  { memberId: string; material: PortalMaterialResponse },
  { memberId: string; file: File; category?: string; description?: string },
  { rejectValue: ApiErrorResponse }
>("portal/uploadMaterial", async ({ memberId, file, category, description }, { rejectWithValue }) => {
  try {
    const form = new FormData();
    form.append("file", file);
    if (category) {
      form.append("category", category);
    }
    if (description) {
      form.append("description", description);
    }
    const response = await portalAxios.post<PortalMaterialResponse>(
      `/portal/members/${memberId}/materials`,
      form,
    );
    return { memberId, material: response.data };
  } catch (error) {
    return rejectWithValue(toApiError(error));
  }
});

/** Deletes one of the member's own uploads. */
export const deletePortalMaterial = createAsyncThunk<
  { memberId: string; materialId: string },
  { memberId: string; materialId: string },
  { rejectValue: ApiErrorResponse }
>("portal/deleteMaterial", async ({ memberId, materialId }, { rejectWithValue }) => {
  try {
    await portalAxios.delete(`/portal/members/${memberId}/materials/${materialId}`);
    return { memberId, materialId };
  } catch (error) {
    return rejectWithValue(toApiError(error));
  }
});

/** Fetches a short-lived presigned URL and hands the file to the browser. */
export const downloadPortalMaterial = createAsyncThunk<
  void,
  { memberId: string; materialId: string },
  { rejectValue: ApiErrorResponse }
>("portal/downloadMaterial", async ({ memberId, materialId }, { rejectWithValue }) => {
  try {
    const response = await portalAxios.get<MaterialDownloadUrlResponse>(
      `/portal/members/${memberId}/materials/${materialId}/download-url`,
    );
    // The presigned URL carries an attachment content-disposition, so a plain
    // navigation saves the file under its original name.
    window.location.assign(response.data.url);
  } catch (error) {
    return rejectWithValue(toApiError(error));
  }
});

/** Future occurrences of open Sessions the target member (self or ward) can book (today..+28 days). */
export const fetchBookableOccurrences = createAsyncThunk<
  { memberId: string; occurrences: BookableOccurrenceResponse[] },
  { memberId: string },
  { rejectValue: ApiErrorResponse }
>("portal/fetchBookableOccurrences", async ({ memberId }, { rejectWithValue }) => {
  try {
    const response = await portalAxios.get<BookableOccurrenceResponse[]>(
      `/portal/members/${memberId}/bookable-occurrences`,
    );
    return { memberId, occurrences: response.data };
  } catch (error) {
    return rejectWithValue(toApiError(error));
  }
});

/** Books the target member a seat on one occurrence; the seat is Confirmed or Requested. */
export const bookOccurrence = createAsyncThunk<
  { memberId: string; seat: OccurrenceSeatResponse },
  { memberId: string; request: BookOccurrenceRequest },
  { rejectValue: ApiErrorResponse }
>("portal/bookOccurrence", async ({ memberId, request }, { rejectWithValue }) => {
  try {
    const response = await portalAxios.post<OccurrenceSeatResponse>(
      `/portal/members/${memberId}/bookings`,
      request,
    );
    return { memberId, seat: response.data };
  } catch (error) {
    return rejectWithValue(toApiError(error));
  }
});
